import { FC, useCallback } from "react"
import { LogIn } from "lucide-react"
import { useFetchBrandAdminsQuery } from "../../../redux/queries/loan-officer"
import { masquerade, selectUser } from "../../../redux/slices/auth"
import { useAppDispatch, useAppSelector } from "../../hooks"
import { BrandAdmin } from "../../../types"

interface Props {
  brandId: string
  canMasquerade: boolean
}

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(
      0
    )}`.toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

const BrandAdminRow: FC<{
  admin: BrandAdmin
  canMasquerade: boolean
  onMasquerade: (uid: string) => void
}> = ({ admin, canMasquerade, onMasquerade }) => (
  <div className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 rounded-md">
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-medium">
        {getInitials(admin.name)}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900">{admin.name}</p>
        <p className="text-xs text-gray-500">{admin.email}</p>
      </div>
    </div>
    {canMasquerade && (
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary-600 px-2 py-1 rounded hover:bg-primary-50 transition-colors"
        onClick={() => onMasquerade(String(admin.uid))}
        title="Masquerade"
      >
        <LogIn size={14} />
        Masquerade
      </button>
    )}
  </div>
)

const BrandAdminsList: FC<Props> = ({ brandId, canMasquerade }) => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectUser)?.user
  const { data: admins, isLoading } = useFetchBrandAdminsQuery(brandId)

  const onMasquerade = useCallback(
    (uid: string) => {
      if (user?.id !== uid && user?.is_masq !== "1") {
        dispatch(masquerade(uid))
      }
    },
    [dispatch, user]
  )

  if (isLoading || !admins?.length) return null

  return (
    <div className="mb-6 border border-gray-200 rounded-lg">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <h3 className="text-sm font-medium text-gray-700">
          Brand Admins{" "}
          <span className="text-gray-400 font-normal">({admins.length})</span>
        </h3>
      </div>
      <div className="divide-y divide-gray-100">
        {admins.map(admin => (
          <BrandAdminRow
            key={admin.uid}
            admin={admin}
            canMasquerade={canMasquerade}
            onMasquerade={onMasquerade}
          />
        ))}
      </div>
    </div>
  )
}

export default BrandAdminsList
